import React, {
  ChangeEvent,
  ChangeEventHandler,
  MouseEvent,
  useMemo,
  useState,
} from "react";
import { FormCardContainer } from "../FormCard/styles";
import { IntroCardv2 } from "./components/IntroCardv2";
import {
  Dialog,
  DialogHeader,
  DialogContent,
  DialogActions,
} from "../../../dialog";
import { Grid } from "../../../Grid";
import { Button } from "../../../Button";
// import { Prompt } from "../FormCard/components/FormModal";

import { Prompt, FormModal } from "./components/FormModal";
import {
  CompletionCard,
  CompletionCardType,
} from "../FormCard/components/CompletionCard";

type FormCardv2Props = {
  isActive: boolean;
  isAssignmentComplete: boolean;
  isAssignmentFailed: boolean;
  showResetLPConfirmationBeforeStartMessage: string | undefined;
  isGradedQuiz: boolean;
  isUngradedQuiz: boolean;
  isSurvey: boolean;
  title: string;
  attempts: number;
  maxAttempts: number | undefined;
  promptIsCorrect: boolean;
  goBackStr: string;
  continueStr: string;
  correctStr: string;
  incorrectStr: string;
  failedStr: string;
  tryAgainStr: string;
  attemptStr: string;
  correctAnswersStr: string;
  quizCompleteStr: string;
  surveyCompleteStr: string;
  checkAnswerStr: string;
  promptIndex: number;
  prompts: Prompt[];
  retryLoading: boolean;
  onSubmitChoice: (
    promptId: string,
    choiceIds: Array<string>,
    choiceInput?: string
  ) => Promise<void>;
  onCardActive: () => void;
  onStartForm: () => void;
  retryForm: () => Promise<any>;
};

export function FormCardv2({
  isActive,
  isAssignmentComplete,
  isAssignmentFailed,
  isGradedQuiz,
  promptIsCorrect,
  showResetLPConfirmationBeforeStartMessage,
  isSurvey,
  title,
  attempts,
  maxAttempts,
  goBackStr,
  continueStr,
  correctStr,
  incorrectStr,
  failedStr,
  tryAgainStr,
  attemptStr,
  correctAnswersStr,
  quizCompleteStr,
  surveyCompleteStr,
  checkAnswerStr,
  prompts,
  retryLoading,
  onCardActive,
  onSubmitChoice: _onSubmitChoice,
  promptIndex: _promptIndex,
  retryForm,
  //   This is only for analytics.
  onStartForm: _onStartForm,
}: FormCardv2Props) {
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [formModalOpen, setFormModalOpen] = useState(false);
  const [promptIndex, setCurrentPromptIndex] = useState(_promptIndex);
  const [selectedChoiceIds, setSelectedChoiceIds] = useState<Array<string>>([]);
  const [submittedChoiceIds, setSubmittedChoiceIds] = useState<Array<string>>(
    []
  );
  const [input, setInput] = useState("");
  const [isFormSubmitting, setIsFormSubmitting] = useState(false);

  const prompt = prompts[promptIndex];

  const [isReview, setIsReview] = useState(false);

  const promptCount = prompts.length;

  const choices = useMemo(() => {
    return prompts[promptIndex].choices;
  }, [prompts, promptIndex]);

  const hasAttemptsRemaining =
    isGradedQuiz &&
    typeof attempts === "number" &&
    typeof maxAttempts === "number"
      ? attempts < maxAttempts
      : true;

  const correctPromptCount = isSurvey
    ? undefined
    : prompts.filter(
        ({ choices }) =>
          choices.some(({ isCorrect }) => isCorrect === true) &&
          !choices.some(({ isFailed }) => isFailed !== false)
      ).length;

  const inputChoices = useMemo(() => {
    return prompts[promptIndex]?.choices
      ?.map((choice) => (choice.type === "INPUT" ? choice.id : null))
      .filter(Boolean) as string[];
  }, [prompts, promptIndex]);

  const hasInputChoice = !!inputChoices.length;

  // const buttonDisabled =
  // (!isLoading && isReview) || // NOTE: boolean logic has became unnecessarily complex should we simplify this?
  // (hasInputChoice
  //   ? !!trimmedInput && trimmedInput.length <= 1000 // is input valid
  //   : !!(selectedChoiceIds.length || submittedChoiceIds.length)); // is choice valid

  const buttonDisabled = useMemo(() => {
    if (isFormSubmitting) {
      return true;
    }

    if (isReview) {
      return false;
    }

    // for survey
    if (hasInputChoice) {
      return !input || input.length > 1000;
    } else {
      if (selectedChoiceIds.length < 1 && submittedChoiceIds.length < 1) {
        return true;
      }
    }

    return false;
  }, [
    isFormSubmitting,
    input,
    isReview,
    hasInputChoice,
    selectedChoiceIds,
    submittedChoiceIds,
  ]);

  const onChangeInput: ChangeEventHandler<HTMLTextAreaElement> = (e) => {
    setInput(e.target.value.substring(0, 1000));
    setSelectedChoiceIds([]);
  };

  const onRetryForm = async (e: MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    // setRetryLoading(true);
    try {
      await retryForm();
    } finally {
      // setRetryLoading(false);
      setCurrentPromptIndex(0);
      resetChoices();
      setFormModalOpen(true);
    }
  };

  const onConfirmAccept = () => {
    setShowConfirmModal(false);
    setFormModalOpen(true);
    _onStartForm?.();
  };

  const onConfirmClose = () => {
    setShowConfirmModal(false);
  };

  const resetChoices = () => {
    setSelectedChoiceIds([]);
    setSubmittedChoiceIds([]);
    setInput("");
  };

  const onNextPrompt = () => {
    resetChoices();
    setCurrentPromptIndex((prev) => prev + 1);
  };

  const onReviewClicked = () => {
    setIsReview(true);
    setFormModalOpen(true);
    setCurrentPromptIndex(0);
  };

  const onRetryPrompt = () => {
    resetChoices();
  };

  const onSubmit = async () => {
    if (buttonDisabled) {
      return;
    }

    const nextOrClose = () => {
      if (promptIndex + 1 === promptCount) {
        onCloseForm();
      } else {
        onNextPrompt();
      }
    };

    if (isReview) {
      return nextOrClose();
    }

    if (isSurvey) {
      await onSubmitChoice();

      return nextOrClose();
    }

    if (!submittedChoiceIds?.length) {
      return onSubmitChoice();
    }

    // At this point the choice was submitted
    if (promptIsCorrect) {
      return nextOrClose();
    }

    if (isGradedQuiz) {
      nextOrClose();
    } else {
      onRetryPrompt();
    }
  };

  const onStartForm = () => {
    if (showResetLPConfirmationBeforeStartMessage) {
      setShowConfirmModal(true);
    } else {
      setFormModalOpen(true);
    }
    _onStartForm?.();
  };

  const onChangeChoice = (
    e: ChangeEvent<HTMLInputElement>,
    value: string,
    isMultiple?: boolean
  ) => {
    if (isMultiple) {
      if (selectedChoiceIds.includes(value)) {
        setSelectedChoiceIds(
          selectedChoiceIds.filter((item) => item !== value)
        );
      } else {
        setSelectedChoiceIds([value, ...selectedChoiceIds]);
      }
    } else {
      setSelectedChoiceIds([value]);
    }
  };

  const onSubmitChoice = async () => {
    setIsFormSubmitting(true); // Start loading

    const choiceIds = hasInputChoice ? inputChoices : selectedChoiceIds;
    const choiceInput = hasInputChoice ? input : undefined;
    if (!prompt || !choiceIds.length || (hasInputChoice && !choiceInput)) {
      return;
    }

    try {
      await _onSubmitChoice(prompt.id, choiceIds, choiceInput);
      setSubmittedChoiceIds(choiceIds);
    } finally {
      setIsFormSubmitting(false); // End loading
    }
  };

  const onCloseForm = () => {
    setFormModalOpen(false);
    if (isReview) {
      setIsReview(false);
    }
    setInput("");
  };

  let as: CompletionCardType;
  if (isAssignmentFailed) {
    as = CompletionCardType.FAILED;
  } else if (isSurvey) {
    as = CompletionCardType.SURVEY_SUCCEED;
  } else if (isGradedQuiz) {
    as = CompletionCardType.GRADED_QUIZ_SUCCEED;
  } else {
    as = CompletionCardType.UNGRADED_QUIZ_SUCCEED;
  }

  // Do we need !isFormModalOpen?
  if (isAssignmentComplete && !formModalOpen && !isReview) {
    return (
      <CompletionCard
        as={as}
        componentProps={{
          isActive,
          hasAttemptsRemaining,
          attempts,
          maxAttempts,
          correctPromptCount,
          failedStr,
          tryAgainStr,
          attemptStr,
          correctAnswersStr,
          quizCompleteStr,
          surveyCompleteStr,
          continueStr,
          retryLoading,
          onRetryForm,
          onReviewClicked,
          // Completion card is last card so on next click we reset the choices
          onNextCard: resetChoices,
          onCardActive,
        }}
      />
    );
  }

  return (
    <FormCardContainer>
      <Dialog open={showConfirmModal}>
        <DialogHeader onClose={onConfirmClose} />
        <DialogContent dividers={false}>
          <Grid container item md={12}>
            <Grid item md={12}>
              {showResetLPConfirmationBeforeStartMessage}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Grid container spacing={2}>
            <Grid item md={6}>
              <Button
                type="button"
                text={goBackStr}
                align="center"
                color="utility"
                onClick={onConfirmClose}
              />
            </Grid>
            <Grid item md={6}>
              <Button
                type="button"
                text={continueStr}
                align="center"
                onClick={onConfirmAccept}
              />
            </Grid>
          </Grid>
        </DialogActions>
      </Dialog>
      <IntroCardv2
        isActive={isActive}
        onCardActive={onCardActive}
        isGradedQuiz={isGradedQuiz}
        isSurvey={isSurvey}
        title={title}
        isStarted={prompt.isCompleted ?? promptIndex > 0}
        onStartForm={onStartForm}
      />
      <FormModal
        isOpen={formModalOpen}
        isReview={isReview}
        promptIndex={promptIndex}
        promptCount={promptCount}
        prompt={prompt}
        buttonDisabled={buttonDisabled}
        checkAnswerStr={checkAnswerStr}
        tryAgainStr={tryAgainStr}
        choices={choices}
        selectedChoiceIds={selectedChoiceIds}
        submittedChoiceIds={submittedChoiceIds}
        input={input}
        isSurvey={isSurvey}
        isGradedQuiz={isGradedQuiz}
        hasInputChoice={hasInputChoice}
        isFormSubmitting={isFormSubmitting}
        areChoicesDisabled={!!submittedChoiceIds.length}
        continueStr={continueStr}
        correctStr={correctStr}
        promptIsCorrect={promptIsCorrect}
        incorrectStr={incorrectStr}
        onCloseForm={onCloseForm}
        onSubmit={onSubmit}
        onChangeChoice={onChangeChoice}
        onChangeInput={onChangeInput}
      />
    </FormCardContainer>
  );
}
